/**
 * Student learning profile - requires sign-in. Cached locally; synced to Supabase.
 */

import { JYOTISHA_MARGA_TAB_GROUPS } from '../data/jyotishaMargaTabGroups';
import { readLastJyotishaTopic } from './jyotishaHomeHub';
import {
  getActiveStudentUserId,
  isStudentProfileWritable,
  setActiveStudentUserId,
} from '../lib/supabase/studentProfileSession';
import {
  fetchStudentProfileFromSupabase,
  mergeStudentProfileStates,
  pushStudentDisplayNameToSupabase,
  pushStudentAvatarToSupabase,
  pushStudentProfileToSupabase,
} from '../lib/supabase/studentProfileSync';
import { isAllowedStudentAvatarUrl, isExplicitStudentAvatarChoice, normalizeStudentAvatarUrl, DEFAULT_STUDENT_AVATAR_URL, resolveProfileAvatarUrl } from '../data/studentProfileAvatars';
import { inferGoalType, computeGoalProgress, calculateDailyStudyTarget } from './learningGoalProgress';
import { applyComputedStudentLevel } from './studentLevelProgress';
import { canonicalJyotishaTopicId } from './jyotishaTopicCanonical';
import {
  DEFAULT_PRIMARY_SCHOOL_SLUG,
  isPrimarySchoolSlug,
  resolvePrimarySchoolSlug,
} from 'utils/primarySchool';
import {
  DEFAULT_THEME_PREFERENCE,
  isThemePreference,
  validateLocationPatch,
  DEFAULT_LANGUAGE_PREFERENCE,
  isLanguagePreference,
} from './userProfilePreferences';
import {
  logLessonCompletedFromTopicKey,
  logLessonStartedFromTopicKey,
  logSettingsUpdated,
} from 'shared/lib/learnerActivityLog';
import { addDaysToDayKey, todayKeyInZone } from 'shared/lib/timezone';
import { getStoredUserTimezone } from 'shared/lib/getStoredUserTimezone';

const STORAGE_KEY = 'takshify.parashari.studentProfile';
const PROFILE_VERSION = 1;

/** @typedef {'not_started' | 'in_progress' | 'completed'} TopicStatus */

/**
 * @typedef {Object} TopicProgress
 * @property {TopicStatus} status
 * @property {string} [lastVisitedAt]
 * @property {number} [scrollY]
 * @property {number} [timeSpentSeconds]
 * @property {string} [completedAt]
 */

/**
 * @typedef {Object} StudentHighlight
 * @property {string} id
 * @property {string} topicId
 * @property {string} text
 * @property {string} note
 * @property {'amber' | 'violet' | 'cyan'} color
 * @property {string} createdAt
 * @property {boolean} inRevisionQueue
 */

/**
 * @typedef {Object} StudentBookmark
 * @property {string} topicId
 * @property {string} addedAt
 */

/**
 * @typedef {'topics' | 'minutes' | 'custom'} LearningGoalType
 * @typedef {Object} LearningGoal
 * @property {string} id
 * @property {LearningGoalType} [goalType]
 * @property {string} title
 * @property {string} [titleHi]
 * @property {string[]} [targetTopicIds]
 * @property {number} [targetCount]
 * @property {number} [targetMinutesPerDay]
 * @property {string} [deadline]
 * @property {boolean} completed
 * @property {string} createdAt
 */

/**
 * @typedef {Object} StudentProfileState
 * @property {number} version
 * @property {{ displayName: string; avatarUrl: string | null; studentLevel: string; interests: string[]; dailyGoalMinutes: number; primarySchoolSlug: import('utils/primarySchool').PrimarySchoolSlug; themePreference: import('./userProfilePreferences').ThemePreference; languagePreference: import('./userProfilePreferences').LanguagePreference; location: import('./userProfilePreferences').UserLocationPreference | null; onboardingCompletedAt: string | null }} profile
 * @property {Record<string, TopicProgress>} topicProgress
 * @property {StudentHighlight[]} highlights
 * @property {StudentBookmark[]} bookmarks
 * @property {LearningGoal[]} goals
 * @property {{ dailyMinutes: Record<string, number>; lastStudyDate: string | null; currentStreak: number; longestStreak: number }} analytics
 * @property {{ topicId: string; visitedAt: string }[]} recentTopics
 * @property {string} [updatedAt] ISO timestamp - last local edit
 * @property {string} [lastSyncedAt] ISO timestamp - last successful cloud push
 * @property {boolean} [profileHydrationComplete] true after cloud profile load on sign-in
 */

/** All syllabus topic ids for progress calculations. */
export const SYLLABUS_TOPIC_IDS = JYOTISHA_MARGA_TAB_GROUPS.flatMap((g) => g.tabs.map((t) => t.id));

let syncTimer = null;
let syncInFlight = false;
let syncPending = false;

function hasUnsyncedLocalChanges(state) {
  return Boolean(state.updatedAt && (!state.lastSyncedAt || state.updatedAt > state.lastSyncedAt));
}

/** @param {StudentProfileState} state @param {{ skipSync?: boolean; skipUpdatedAtBump?: boolean }} [opts] */
function markSynced(state, opts = {}) {
  writeStudentProfileCache(
    { ...state, lastSyncedAt: new Date().toISOString() },
    { skipSync: true, skipUpdatedAtBump: true, ...opts }
  );
}

async function runCloudSync() {
  const userId = getActiveStudentUserId();
  if (!userId) return;
  if (syncInFlight) {
    syncPending = true;
    return;
  }
  syncInFlight = true;
  syncPending = false;
  try {
    const state = readCachedProfile();
    await pushStudentProfileToSupabase(userId, state);
    markSynced(state);
  } catch (err) {
    console.warn('Student profile cloud sync failed:', err);
  } finally {
    syncInFlight = false;
    if (syncPending) scheduleCloudSync(0);
  }
}

function scheduleCloudSync(delayMs = 600) {
  const userId = getActiveStudentUserId();
  if (!userId) return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void runCloudSync();
  }, delayMs);
}

/** Push local profile to Supabase immediately (e.g. after delete). */
export function flushStudentProfileSync() {
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  return runCloudSync();
}

function todayKey() {
  return todayKeyInZone(getStoredUserTimezone());
}

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** @param {string | null | undefined} topicId */
function canonTopicId(topicId) {
  if (!topicId) return topicId;
  return canonicalJyotishaTopicId(topicId) ?? topicId;
}

/** @param {TopicProgress} a @param {TopicProgress} b */
function mergeTopicProgressEntry(a, b) {
  const rank = { not_started: 0, in_progress: 1, completed: 2 };
  const status = (rank[a.status] ?? 0) >= (rank[b.status] ?? 0) ? a.status : b.status;
  const lastVisitedAt = [a.lastVisitedAt, b.lastVisitedAt].filter(Boolean).sort().pop() ?? undefined;
  const completedAt = [a.completedAt, b.completedAt].filter(Boolean).sort().pop() ?? undefined;
  const scrollY = Math.max(a.scrollY ?? 0, b.scrollY ?? 0) || undefined;
  const timeSpentSeconds = (a.timeSpentSeconds ?? 0) + (b.timeSpentSeconds ?? 0) || undefined;
  return { status, lastVisitedAt, completedAt, scrollY, timeSpentSeconds };
}

/** @param {Record<string, TopicProgress>} topicProgress */
function migrateTopicProgressKeys(topicProgress) {
  const out = {};
  for (const [key, value] of Object.entries(topicProgress || {})) {
    const canon = canonTopicId(key);
    if (!canon) continue;
    out[canon] = out[canon] ? mergeTopicProgressEntry(out[canon], value) : { ...value };
  }
  return out;
}

/** @param {StudentProfileState} state */
function migrateProfileTopicIds(state) {
  return {
    ...state,
    topicProgress: migrateTopicProgressKeys(state.topicProgress),
    highlights: (state.highlights ?? []).map((h) => ({ ...h, topicId: canonTopicId(h.topicId) })),
    bookmarks: (state.bookmarks ?? []).map((b) => ({ ...b, topicId: canonTopicId(b.topicId) })),
    recentTopics: (state.recentTopics ?? []).map((r) => ({ ...r, topicId: canonTopicId(r.topicId) })),
    goals: (state.goals ?? []).map((g) => ({
      ...g,
      targetTopicIds: g.targetTopicIds?.map((id) => canonTopicId(id)).filter(Boolean),
    })),
  };
}

/** @param {string | null | undefined} cachedAvatarUrl */
function normalizeCachedAvatarUrl(cachedAvatarUrl) {
  const normalized = normalizeStudentAvatarUrl(cachedAvatarUrl);
  if (!normalized) return null;
  if (isExplicitStudentAvatarChoice(normalized)) return normalized;
  if (normalized === DEFAULT_STUDENT_AVATAR_URL) return null;
  return normalized;
}

/** @returns {StudentProfileState} */
function readCachedProfile() {
  if (typeof window === 'undefined') return createDefaultProfileState();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return createDefaultProfileState();
    const parsed = JSON.parse(raw);
    const defaults = createDefaultProfileState();
    const parsedProfile = parsed.profile ?? {};
    const { preferredHomePage: _legacyHome, ...profileWithoutLegacy } = parsedProfile;
    const merged = {
      ...defaults,
      ...parsed,
      version: PROFILE_VERSION,
      profile: {
        ...defaults.profile,
        ...profileWithoutLegacy,
        avatarUrl: normalizeCachedAvatarUrl(parsedProfile.avatarUrl),
        primarySchoolSlug: resolvePrimarySchoolSlug(parsedProfile),
      },
      analytics: { ...defaults.analytics, ...(parsed.analytics ?? {}) },
    };
    return migrateProfileTopicIds(merged);
  } catch {
    return createDefaultProfileState();
  }
}

/** Load profile from Supabase after sign-in; merges one-time local data if cloud is empty. */
export async function hydrateStudentProfileForUser(userId) {
  setActiveStudentUserId(userId);
  const localBefore = readCachedProfile();

  let cloud = createDefaultProfileState();
  try {
    cloud = await fetchStudentProfileFromSupabase(userId);
  } catch (err) {
    console.warn('Failed to load student profile from Supabase:', err);
  }

  let merged = applyComputedStudentLevel(mergeStudentProfileStates(cloud, localBefore));
  const localAfterMerge = readCachedProfile();
  const localStillPending =
    Boolean(localAfterMerge.updatedAt) &&
    (!localAfterMerge.lastSyncedAt || localAfterMerge.updatedAt > localAfterMerge.lastSyncedAt);
  if (localStillPending) {
    merged = applyComputedStudentLevel(mergeStudentProfileStates(cloud, localAfterMerge));
  }

  const cloudAvatar = normalizeStudentAvatarUrl(cloud.profile.avatarUrl);
  const mergedAvatar = normalizeStudentAvatarUrl(merged.profile.avatarUrl);
  const resolvedAvatarUrl = resolveProfileAvatarUrl(merged.profile.avatarUrl);
  const needsDefaultAvatar = !mergedAvatar && !cloudAvatar && resolvedAvatarUrl;
  if (needsDefaultAvatar) {
    merged = {
      ...merged,
      profile: { ...merged.profile, avatarUrl: resolvedAvatarUrl },
      updatedAt: new Date().toISOString(),
    };
  }

  writeStudentProfileCache(
    { ...merged, profileHydrationComplete: true },
    { skipSync: true, skipUpdatedAtBump: true },
  );

  if (hasUnsyncedLocalChanges(merged) || needsDefaultAvatar) {
    scheduleCloudSync(0);
  }
  return merged;
}

/** Clear session on sign-out - learning data is not available offline. */
export function clearStudentProfileSession() {
  setActiveStudentUserId(null);
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = null;
  syncInFlight = false;
  syncPending = false;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent('student-profile-updated'));
  }
}

/** @returns {boolean} */
export function isProfileHydrationComplete() {
  return Boolean(readStudentProfile().profileHydrationComplete);
}

export { isStudentProfileWritable, getActiveStudentUserId };

/** @returns {StudentProfileState} */
export function createDefaultProfileState() {
  return {
    version: PROFILE_VERSION,
    profile: {
      displayName: '',
      avatarUrl: null,
      studentLevel: 'beginner',
      interests: [],
      dailyGoalMinutes: 20,
      primarySchoolSlug: DEFAULT_PRIMARY_SCHOOL_SLUG,
      themePreference: DEFAULT_THEME_PREFERENCE,
      languagePreference: DEFAULT_LANGUAGE_PREFERENCE,
      location: null,
      onboardingCompletedAt: null,
    },
    topicProgress: {},
    highlights: [],
    bookmarks: [],
    goals: [],
    analytics: {
      dailyMinutes: {},
      lastStudyDate: null,
      currentStreak: 0,
      longestStreak: 0,
    },
    recentTopics: [],
  };
}

/** @returns {StudentProfileState} */
export function readStudentProfile() {
  if (!isStudentProfileWritable()) return createDefaultProfileState();
  return readCachedProfile();
}

/** @param {StudentProfileState} state @param {{ skipSync?: boolean; skipUpdatedAtBump?: boolean }} [opts] */
function writeStudentProfileCache(state, opts = {}) {
  if (typeof window === 'undefined') return;
  if (!isStudentProfileWritable()) return;
  try {
    const stamped = opts.skipUpdatedAtBump
      ? state
      : { ...state, updatedAt: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stamped));
    window.dispatchEvent(new CustomEvent('student-profile-updated'));
    if (!opts.skipSync) scheduleCloudSync();
  } catch {
    /* quota / private mode */
  }
}

/** @param {StudentProfileState} state */
export function writeStudentProfile(state) {
  writeStudentProfileCache(state);
}

/** @param {(state: StudentProfileState) => StudentProfileState} updater */
export function updateStudentProfile(updater) {
  if (!isStudentProfileWritable()) return createDefaultProfileState();
  const prev = readCachedProfile();
  const next = applyComputedStudentLevel(updater(prev));
  if (next === prev) return next;
  writeStudentProfileCache(next);
  return next;
}

export function getTopicProgress(topicId) {
  const state = readStudentProfile();
  const id = canonTopicId(topicId);
  return state.topicProgress[id] ?? { status: 'not_started' };
}

/** @param {string} topicId @param {Partial<TopicProgress>} patch */
export function saveTopicProgress(topicId, patch) {
  const id = canonTopicId(topicId);
  if (!id) return;
  updateStudentProfile((state) => {
    const prev = state.topicProgress[id] ?? { status: 'not_started' };
    const merged = { ...prev, ...patch };
    if (patch.status === 'in_progress' && prev.status === 'not_started') {
      merged.lastVisitedAt = patch.lastVisitedAt ?? new Date().toISOString();
    }
    if (patch.status === 'completed') {
      merged.completedAt = patch.completedAt ?? new Date().toISOString();
    }
    return {
      ...state,
      topicProgress: { ...state.topicProgress, [id]: merged },
    };
  });
}

/** @param {string} topicId @param {number} scrollY */
export function saveTopicScrollPosition(topicId, scrollY) {
  if (!topicId || typeof scrollY !== 'number') return;
  saveTopicProgress(topicId, { scrollY, lastVisitedAt: new Date().toISOString() });
}

/** @param {string} topicId @param {number} seconds */
export function addTopicStudyTime(topicId, seconds) {
  const id = canonTopicId(topicId);
  if (!id || seconds <= 0) return;
  const day = todayKey();
  updateStudentProfile((state) => {
    const prev = state.topicProgress[id] ?? { status: 'not_started' };
    const minutes = seconds / 60;
    const dailyMinutes = { ...state.analytics.dailyMinutes };
    dailyMinutes[day] = (dailyMinutes[day] ?? 0) + minutes;

    const lastDate = state.analytics.lastStudyDate;
    let { currentStreak, longestStreak } = state.analytics;
    if (lastDate !== day) {
      const yesterdayKey = addDaysToDayKey(day, -1);
      if (lastDate === yesterdayKey) currentStreak += 1;
      else if (lastDate !== day) currentStreak = 1;
      longestStreak = Math.max(longestStreak, currentStreak);
    }

    return {
      ...state,
      topicProgress: {
        ...state.topicProgress,
        [id]: {
          ...prev,
          status: prev.status === 'not_started' ? 'in_progress' : prev.status,
          timeSpentSeconds: (prev.timeSpentSeconds ?? 0) + seconds,
          lastVisitedAt: new Date().toISOString(),
        },
      },
      analytics: {
        dailyMinutes,
        lastStudyDate: day,
        currentStreak,
        longestStreak,
      },
    };
  });
}

/** @param {string} topicId */
export function markTopicVisited(topicId) {
  const id = canonTopicId(topicId);
  if (!id) return;
  const wasNotStarted = (getTopicProgress(id).status ?? 'not_started') === 'not_started';
  updateStudentProfile((state) => {
    const prev = state.topicProgress[id] ?? { status: 'not_started' };
    const recent = [{ topicId: id, visitedAt: new Date().toISOString() }, ...state.recentTopics.filter((r) => r.topicId !== id)].slice(0, 8);
    return {
      ...state,
      topicProgress: {
        ...state.topicProgress,
        [id]: {
          ...prev,
          status: prev.status === 'completed' ? 'completed' : 'in_progress',
          lastVisitedAt: new Date().toISOString(),
        },
      },
      recentTopics: recent,
    };
  });
  if (wasNotStarted && isStudentProfileWritable()) {
    logLessonStartedFromTopicKey(id);
  }
}

/** @param {string} topicId */
export function markTopicCompleted(topicId) {
  const id = canonTopicId(topicId);
  const wasCompleted = id ? getTopicProgress(id).status === 'completed' : true;
  saveTopicProgress(topicId, { status: 'completed', completedAt: new Date().toISOString() });
  if (id && !wasCompleted && isStudentProfileWritable()) {
    logLessonCompletedFromTopicKey(id);
  }
}

/**
 * Mark a lesson complete (canonical topic id). No-op if already completed or not signed in.
 * @param {string} topicId
 * @returns {boolean} whether the topic is completed after this call
 */
export function completeLessonTopic(topicId) {
  const id = canonTopicId(topicId);
  if (!id || !isStudentProfileWritable()) return false;
  if (getTopicProgress(id).status === 'completed') return true;
  markTopicCompleted(id);
  return true;
}

/** @param {Record<string, TopicProgress>} base @param {Record<string, TopicProgress>} overlay */
export function mergeTopicProgressMaps(base, overlay) {
  const out = { ...(base ?? {}) };
  for (const [key, value] of Object.entries(overlay ?? {})) {
    out[key] = out[key] ? mergeTopicProgressEntry(out[key], value) : { ...value };
  }
  return out;
}

/** @param {string} topicId @param {string} text @param {string} [note] @param {'amber' | 'violet' | 'cyan'} [color] */
export function addHighlight(topicId, text, note = '', color = 'amber') {
  const id = canonTopicId(topicId);
  const trimmed = text.trim();
  if (!id || !trimmed) return null;
  const highlight = {
    id: generateId(),
    topicId: id,
    text: trimmed,
    note: note.trim(),
    color,
    createdAt: new Date().toISOString(),
    inRevisionQueue: Boolean(note.trim()),
  };
  updateStudentProfile((state) => ({
    ...state,
    highlights: [highlight, ...state.highlights],
  }));
  return highlight;
}

/** @param {string} highlightId @param {Partial<StudentHighlight>} patch */
export function updateHighlight(highlightId, patch) {
  updateStudentProfile((state) => ({
    ...state,
    highlights: state.highlights.map((h) => (h.id === highlightId ? { ...h, ...patch } : h)),
  }));
}

/** @param {string} highlightId */
export function removeHighlight(highlightId) {
  if (!highlightId) return;
  updateStudentProfile((state) => ({
    ...state,
    highlights: state.highlights.filter((h) => h.id !== highlightId),
  }));
  void flushStudentProfileSync();
}

/** @param {string} topicId */
export function removeBookmark(topicId) {
  const id = canonTopicId(topicId);
  if (!id) return;
  updateStudentProfile((state) => ({
    ...state,
    bookmarks: state.bookmarks.filter((b) => b.topicId !== id),
  }));
  void flushStudentProfileSync();
}

/** @param {string} topicId */
export function getHighlightsForTopic(topicId) {
  const id = canonTopicId(topicId);
  return readStudentProfile().highlights.filter((h) => h.topicId === id);
}

/** @param {string} topicId */
export function toggleBookmark(topicId) {
  const id = canonTopicId(topicId);
  if (!id) return false;
  let added = false;
  updateStudentProfile((state) => {
    const exists = state.bookmarks.some((b) => b.topicId === id);
    if (exists) {
      return { ...state, bookmarks: state.bookmarks.filter((b) => b.topicId !== id) };
    }
    added = true;
    return {
      ...state,
      bookmarks: [{ topicId: id, addedAt: new Date().toISOString() }, ...state.bookmarks],
    };
  });
  if (!added) void flushStudentProfileSync();
  return added;
}

/** @param {string} topicId */
export function isTopicBookmarked(topicId) {
  const id = canonTopicId(topicId);
  return readStudentProfile().bookmarks.some((b) => b.topicId === id);
}

/** Auto-mark measurable goals complete when progress hits 100%. */
export function syncGoalsAutoComplete() {
  if (!isStudentProfileWritable()) return;
  updateStudentProfile((state) => {
    let changed = false;
    const goals = state.goals.map((g) => {
      if (g.completed || inferGoalType(g) === 'custom') return g;
      const progress = computeGoalProgress(g, state, true);
      if (progress.done) {
        changed = true;
        return { ...g, completed: true };
      }
      return g;
    });
    if (!changed) return state;
    return { ...state, goals };
  });
}

/** @param {Partial<LearningGoal>} goal */
export function addLearningGoal(goal) {
  const goalType = inferGoalType(goal);
  const entry = {
    id: generateId(),
    goalType,
    title: (goal.title ?? '').trim() || (goalType === 'minutes' ? 'Daily study goal' : 'New goal'),
    titleHi: goal.titleHi?.trim() || undefined,
    targetTopicIds: goal.targetTopicIds ?? [],
    targetCount: goal.targetCount,
    targetMinutesPerDay: goal.targetMinutesPerDay,
    deadline: goal.deadline,
    completed: false,
    createdAt: new Date().toISOString(),
  };
  if (goalType === 'minutes' && !entry.targetMinutesPerDay) {
    entry.targetMinutesPerDay = calculateDailyStudyTarget(readStudentProfile());
  }
  if (goalType === 'topics' && entry.targetTopicIds.length && !entry.targetCount) {
    entry.targetCount = entry.targetTopicIds.length;
  }
  updateStudentProfile((state) => ({ ...state, goals: [entry, ...state.goals] }));
  void flushStudentProfileSync();
  return entry;
}

/** @param {string} goalId @param {boolean} completed */
export function setGoalCompleted(goalId, completed) {
  updateStudentProfile((state) => ({
    ...state,
    goals: state.goals.map((g) => (g.id === goalId ? { ...g, completed } : g)),
  }));
}

/** @param {string} goalId */
export function removeLearningGoal(goalId) {
  if (!goalId) return;
  updateStudentProfile((state) => ({
    ...state,
    goals: state.goals.filter((g) => g.id !== goalId),
  }));
  void flushStudentProfileSync();
}

/** @param {Partial<StudentProfileState['profile']>} patch @returns {Promise<void>} */
export async function updateProfileSettings(patch) {
  const { studentLevel: _ignoredLevel, dailyGoalMinutes: _ignoredDaily, ...rest } = patch;

  if (Object.prototype.hasOwnProperty.call(rest, 'avatarUrl')) {
    const nextUrl = rest.avatarUrl?.trim() ? normalizeStudentAvatarUrl(rest.avatarUrl.trim()) : null;
    if (nextUrl && !isAllowedStudentAvatarUrl(nextUrl)) {
      throw new Error('Invalid avatar selection.');
    }
    rest.avatarUrl = nextUrl;
  }

  if (Object.prototype.hasOwnProperty.call(rest, 'primarySchoolSlug')) {
    if (!isPrimarySchoolSlug(rest.primarySchoolSlug)) {
      throw new Error('Invalid primary school.');
    }
  }

  if (Object.prototype.hasOwnProperty.call(rest, 'themePreference')) {
    if (!isThemePreference(rest.themePreference)) {
      throw new Error('Invalid theme preference.');
    }
  }

  if (Object.prototype.hasOwnProperty.call(rest, 'languagePreference')) {
    if (!isLanguagePreference(rest.languagePreference)) {
      throw new Error('Invalid language preference.');
    }
  }

  if (Object.prototype.hasOwnProperty.call(rest, 'onboardingCompletedAt')) {
    const value = rest.onboardingCompletedAt;
    if (value !== null && typeof value !== 'string') {
      throw new Error('Invalid onboarding completion timestamp.');
    }
  }

  const locationPatch = validateLocationPatch(rest);
  if (locationPatch !== undefined) {
    rest.location = locationPatch;
  }

  updateStudentProfile((state) => ({
    ...state,
    profile: { ...state.profile, ...rest },
  }));

  const userId = getActiveStudentUserId();
  if (userId && Object.prototype.hasOwnProperty.call(rest, 'displayName')) {
    await pushStudentDisplayNameToSupabase(userId, rest.displayName ?? '');
  }
  if (userId && Object.prototype.hasOwnProperty.call(rest, 'avatarUrl')) {
    await pushStudentAvatarToSupabase(userId, rest.avatarUrl ?? null);
  }

  await flushStudentProfileSync();

  const settingsKeys = Object.keys(rest);
  if (settingsKeys.length) {
    logSettingsUpdated(settingsKeys);
  }
}

/** Quiz topic ids tracked in topic progress. */
export const QUIZ_TOPIC_IDS = ['quiz-daily', 'quiz-practice'];

export function getQuizActivity() {
  const state = readStudentProfile();
  /** @type {Array<{ topicId: string; status: TopicStatus; lastVisitedAt?: string; completedAt?: string }>} */
  const items = [];

  for (const topicId of QUIZ_TOPIC_IDS) {
    const progress = state.topicProgress[topicId];
    if (progress && progress.status !== 'not_started') {
      items.push({ topicId, ...progress });
    }
  }

  items.sort((a, b) => {
    const ta = a.lastVisitedAt ?? a.completedAt ?? '';
    const tb = b.lastVisitedAt ?? b.completedAt ?? '';
    return tb.localeCompare(ta);
  });

  return items;
}

export function getProgressSummary() {
  const state = readStudentProfile();
  const completed = SYLLABUS_TOPIC_IDS.filter((id) => state.topicProgress[id]?.status === 'completed').length;
  const inProgress = SYLLABUS_TOPIC_IDS.filter((id) => state.topicProgress[id]?.status === 'in_progress').length;
  const total = SYLLABUS_TOPIC_IDS.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, inProgress, total, pct };
}

/** Syllabus topics started but not yet completed, most recent first. */
export function getInProgressTopics(limit = 12) {
  const state = readStudentProfile();
  const items = SYLLABUS_TOPIC_IDS.filter((id) => state.topicProgress[id]?.status === 'in_progress').map((id) => ({
    topicId: id,
    lastVisitedAt: state.topicProgress[id]?.lastVisitedAt ?? null,
    scrollY: state.topicProgress[id]?.scrollY ?? 0,
  }));
  items.sort((a, b) => (b.lastVisitedAt ?? '').localeCompare(a.lastVisitedAt ?? ''));
  return items.slice(0, limit);
}

export function getGroupProgress() {
  const state = readStudentProfile();
  return JYOTISHA_MARGA_TAB_GROUPS.map((group) => {
    const ids = group.tabs.map((t) => t.id);
    const completed = ids.filter((id) => state.topicProgress[id]?.status === 'completed').length;
    return {
      id: group.id,
      label: group.label,
      labelEn: group.labelEn,
      completed,
      total: ids.length,
      pct: ids.length > 0 ? Math.round((completed / ids.length) * 100) : 0,
    };
  });
}

export function getContinueLearningTopic() {
  const state = readStudentProfile();
  const lastId = readLastJyotishaTopic();
  if (lastId) {
    const progress = state.topicProgress[lastId];
    return {
      topicId: lastId,
      scrollY: progress?.scrollY ?? 0,
      lastVisitedAt: progress?.lastVisitedAt ?? null,
      status: progress?.status ?? 'in_progress',
    };
  }
  return null;
}

export function getRevisionQueue() {
  return readStudentProfile().highlights.filter((h) => h.inRevisionQueue || h.note);
}

export function getWeeklyStudyMinutes() {
  const state = readStudentProfile();
  const keys = Object.keys(state.analytics.dailyMinutes).sort().slice(-7);
  return keys.map((day) => ({ day, minutes: Math.round(state.analytics.dailyMinutes[day] ?? 0) }));
}

export function getTotalStudyMinutes() {
  const state = readStudentProfile();
  return Math.round(Object.values(state.analytics.dailyMinutes).reduce((s, m) => s + m, 0));
}

/** Suggest next unread topics from the same group as last visited. */
export function getRecommendedTopics(limit = 3) {
  const state = readStudentProfile();
  const lastId = readLastJyotishaTopic();
  let pool = SYLLABUS_TOPIC_IDS;

  if (lastId) {
    const group = JYOTISHA_MARGA_TAB_GROUPS.find((g) => g.tabs.some((t) => t.id === lastId));
    if (group) {
      const groupIds = group.tabs.map((t) => t.id);
      const incompleteInGroup = groupIds.filter((id) => state.topicProgress[id]?.status !== 'completed');
      if (incompleteInGroup.length > 0) pool = incompleteInGroup;
    }
  }

  const bookmarked = state.bookmarks.map((b) => b.topicId);
  const candidates = [...new Set([...bookmarked, ...pool])].filter(
    (id) => state.topicProgress[id]?.status !== 'completed'
  );
  return candidates.slice(0, limit);
}

export const GOAL_TEMPLATES = [
  {
    goalType: 'topics',
    title: 'Finish Nakshatras (3 lessons)',
    titleHi: 'नक्षत्र अध्याय पूरा करें (३ पाठ)',
    targetTopicIds: ['nakshatras', 'nakshatra-profiles', 'nakshatra-yogatara'],
    targetCount: 3,
  },
  {
    goalType: 'topics',
    title: 'Complete Rashis chapter (3 lessons)',
    titleHi: 'राशि अध्याय पूरा करें (३ पाठ)',
    targetTopicIds: ['rashis-basics', 'rashis-char-sthir-dwiswabhav', 'mool-trikon-rashi'],
    targetCount: 3,
  },
  {
    goalType: 'minutes',
    title: 'Study 20 min daily for 7 days',
    titleHi: '7 दिन रोज़ 20 मिनट अध्ययन',
    targetMinutesPerDay: 20,
    targetCount: 7,
  },
  {
    goalType: 'topics',
    title: 'Complete Prarambh foundations (3 lessons)',
    titleHi: 'प्रारंभ की नींव पूरा करें (३ पाठ)',
    targetTopicIds: ['intro-astrology-branches', 'intro-kundali-types', 'prarambh-navagraha-forces'],
    targetCount: 3,
  },
];
